/**
 * ============================================================================
 * CalibrationTray — Phase 6e frontend (Equipment & Validation Repository).
 * Owner: Claude 3 (Frontend & UX).
 * Route: /equipment/calibrations
 * Purpose: queue of upcoming, overdue, and recently completed calibration
 *          tasks. Three tabs (Due This Week / Overdue / Recently Completed)
 *          drive the visible set; one CTA per row opens the
 *          RecordCalibrationDialog.
 *
 * UX RULES:
 *   - Cards (not table rows) so the screen is usable on mobile.
 *   - Loading: skeleton cards. Error: refined envelope with Retry.
 * ============================================================================ */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CardHeader, PageHero } from '@edms/design-system';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './CalibrationTray.module.css';
import {
  useCalibrationTrayQuery,
  type CalibrationTask,
  type CalibrationTaskBucket,
} from './useCalibrationTrayQuery';
import { CalibrationTaskCard } from './CalibrationTaskCard';
import { RecordCalibrationDialog } from './RecordCalibrationDialog';

const TABS: CalibrationTaskBucket[] = [
  'DUE_THIS_WEEK',
  'OVERDUE',
  'RECENTLY_COMPLETED',
];

const TAB_LABEL: Record<CalibrationTaskBucket, string> = {
  DUE_THIS_WEEK: 'Due this week',
  OVERDUE: 'Overdue',
  RECENTLY_COMPLETED: 'Recently completed',
};

export function CalibrationTray() {
  const { t, i18n } = useTranslation();
  const tasksQ = useCalibrationTrayQuery();
  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

  const [tab, setTab] = useState<CalibrationTaskBucket>('DUE_THIS_WEEK');
  const [target, setTarget] = useState<CalibrationTask | null>(null);

  const all: CalibrationTask[] = tasksQ.data?.items ?? [];
  const counts = tasksQ.data?.counts ?? {
    DUE_THIS_WEEK: 0,
    OVERDUE: 0,
    RECENTLY_COMPLETED: 0,
  };

  const visible = useMemo(
    () => all.filter((task) => task.bucket === tab),
    [all, tab],
  );

  return (
    <div className={wstyles.adminScreen}>
      <PageHero
        eyebrow={t('equipment.calibrationTray.eyebrow', {
          defaultValue: 'Equipment',
        })}
        title={t('equipment.calibrationTray.title', {
          defaultValue: 'Calibration tray',
        })}
        subtitle={t('equipment.calibrationTray.subtitle', {
          defaultValue:
            'Equipment calibrations awaiting recording. Internal calibrations require a supervisor co-signature.',
        })}
      />

      <Card>
        <CardHeader>
          <ul
            className={styles.tabs}
            role="tablist"
            aria-label={t('equipment.calibrationTray.tabsAria', {
              defaultValue: 'Calibration bucket',
            })}
          >
            {TABS.map((k) => {
              const active = tab === k;
              return (
                <li key={k} role="presentation">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={active}
                    aria-controls={`cal-tab-panel-${k}`}
                    id={`cal-tab-${k}`}
                    tabIndex={active ? 0 : -1}
                    className={`${styles.tabButton} ${active ? styles.tabButtonActive : ''}`}
                    onClick={() => setTab(k)}
                  >
                    <span>
                      {t(`equipment.calibrationTray.tabs.${k}`, {
                        defaultValue: TAB_LABEL[k],
                      })}
                    </span>
                    <span className={styles.tabBadge} aria-hidden="true">
                      {nf.format(counts[k] ?? 0)}
                    </span>
                    <span className="sr-only">
                      {t('equipment.calibrationTray.tabsCountAria', {
                        defaultValue: '({{count}} tasks)',
                        count: counts[k] ?? 0,
                      })}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </CardHeader>
        <CardBody>
          <div
            role="tabpanel"
            id={`cal-tab-panel-${tab}`}
            aria-labelledby={`cal-tab-${tab}`}
          >
            {tasksQ.isLoading && (
              <div
                role="status"
                aria-live="polite"
                aria-label={t('common.loading', { defaultValue: 'Loading…' })}
              >
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className={styles.skeletonCard}
                    aria-hidden="true"
                  />
                ))}
              </div>
            )}

            {tasksQ.isError && (
              <div className={styles.errorBanner} role="alert">
                <span>
                  {t('equipment.calibrationTray.errors.loadFailed', {
                    defaultValue: 'Could not load calibration tasks.',
                  })}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => tasksQ.refetch()}
                >
                  {t('equipment.calibrationTray.retry', { defaultValue: 'Retry' })}
                </Button>
              </div>
            )}

            {!tasksQ.isLoading && !tasksQ.isError && visible.length === 0 && (
              <p className={styles.emptyState}>
                {t('equipment.calibrationTray.empty', {
                  defaultValue: 'No calibration tasks in this bucket.',
                })}
              </p>
            )}

            {visible.length > 0 && (
              <ul className={styles.cardList}>
                {visible.map((task) => (
                  <li key={task.task_id}>
                    <CalibrationTaskCard
                      task={task}
                      onRecord={(tk) => setTarget(tk)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </CardBody>
      </Card>

      {target && (
        <RecordCalibrationDialog
          task={target}
          onClose={() => setTarget(null)}
          onRecorded={() => {
            setTarget(null);
            tasksQ.refetch();
          }}
        />
      )}
    </div>
  );
}
